import { NavLink, Navigate, Route, Routes } from "react-router-dom";
import { ArrowRightLeft, GitMerge, History, PhoneCall, RefreshCw, ScanSearch, Settings2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { ProductMapping } from "@/components/billingsync/ProductMapping";
import { BillingSyncSettings } from "@/components/billingsync/BillingSyncSettings";
import { TrackedItemsPreview } from "@/components/billingsync/TrackedItemsPreview";
import { ApplyHistory } from "@/components/billingsync/history/ApplyHistory";
import { ApplyDetail } from "@/components/billingsync/history/ApplyDetail";
import { UsageCharges } from "@/components/billingsync/usage/UsageCharges";
import { TrackedItemsSync } from "@/components/billingsync/autosync/TrackedItemsSync";

const MENU = [
  { to: "product-mapping", label: "Product Mapping", hint: "Platform items → billing products", icon: GitMerge },
  { to: "tracked-items", label: "Tracked Items Preview", hint: "What the sync would add, move, remove", icon: ScanSearch },
  { to: "tracked-items-sync", label: "Tracked Items Sync", hint: "Daily automatic apply to Stax Bill", icon: RefreshCw },
  { to: "apply-history", label: "Apply History", hint: "Every apply and undo, row by row", icon: History },
  { to: "usage-charges", label: "Usage Charges", hint: "WebCDR calls → cycle usage on Stax Bill", icon: PhoneCall },
  { to: "settings", label: "Settings", hint: "Billing database & preview sources", icon: Settings2, superOnly: true },
];

function SubMenu({ isSuper }) {
  return (
    <nav className="space-y-1" data-testid="bs-submenu">
      <div className="px-3 pb-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-400">Services</div>
      {MENU.filter((m) => !m.superOnly || isSuper).map((m) => (
        <NavLink key={m.to} to={`/billing-sync/${m.to}`} data-testid={`bs-menu-${m.to}`}
                 className={({ isActive }) => `group flex items-start gap-2.5 rounded-lg px-3 py-2.5 transition-colors ${isActive ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-white hover:text-slate-900"}`}>
          {({ isActive }) => (<>
            <m.icon className={`mt-0.5 h-4 w-4 shrink-0 ${isActive ? "text-emerald-400" : "text-slate-400 group-hover:text-emerald-600"}`} />
            <span className="min-w-0"><span className="block text-sm font-medium">{m.label}</span>
              <span className={`block truncate text-[11px] ${isActive ? "text-slate-300" : "text-slate-400"}`}>{m.hint}</span></span>
          </>)}
        </NavLink>
      ))}
    </nav>
  );
}

export default function BillingSync() {
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";
  return (
    <div className="mx-auto max-w-[1500px] px-4 py-6" data-testid="billing-sync-page">
      <PageHeader testid="billing-sync-header" title="Billing Sync"
                  titleAccessory={<span className="ml-3 rounded-full bg-emerald-600 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white">Roadmap</span>}
                  subtitle="Keeps platform domains and extensions in step with their Stax Bill products." />
      <div className="grid gap-6 lg:grid-cols-[230px_1fr]">
        <aside className="lg:sticky lg:top-4 lg:self-start">
          <div className="mb-4 flex items-center gap-2 px-3 text-xs text-slate-500"><ArrowRightLeft className="h-3.5 w-3.5 text-emerald-600" /> Platform → Stax Bill</div>
          <SubMenu isSuper={isSuper} />
        </aside>
        <main className="min-w-0">
          <Routes>
            <Route index element={<Navigate to="product-mapping" replace />} />
            <Route path="product-mapping" element={<ProductMapping />} />
            <Route path="tracked-items" element={<TrackedItemsPreview />} />
            <Route path="tracked-items-sync" element={<TrackedItemsSync />} />
            <Route path="apply-history" element={<ApplyHistory />} />
            <Route path="apply-history/:aid" element={<ApplyDetail />} />
            <Route path="usage-charges/*" element={<UsageCharges />} />
            {isSuper && <Route path="settings" element={<BillingSyncSettings />} />}
            <Route path="*" element={<Navigate to="product-mapping" replace />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}
